import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { PendoService } from 'analytics-lib';
import {
  IEntity,
  IEntityMetadata,
  IFileDataSourceApiService,
  IMergeFile,
  INamedEntity,
  IRemoteFileInfo,
  ISharedServiceProviderService,
} from 'interfaces-lib';
import { IIconService } from 'interfaces-lib';
import { IWorkflowService } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { INamedEntityService } from 'named-entity-service-lib';
import { AutoGenIconPipe } from 'pipes-lib';
import { TransformBytesToMB } from 'pipes-lib';
import { AsyncSubject } from 'rxjs';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { TerminologySentenceBuilder } from 'terminology-lib';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { WorkflowTaskRunner } from 'workflow-task-service-lib';
import { runtimeEnvironment } from '../../environments/environment';
import { SimpleTableComponent } from '../common-components/simple-table/simple-table.component';
import { ActionMeta, ISimpleTableColumn, ISimpleTableRow } from '../common-components/simple-table/simple-table.interfaces';
import { IFileListProvider } from '../interfaces/ifilelistprovider';
import { IFileToPathConverter } from '../interfaces/ifiletopathconverter';
import { FileCheckReason, LocalFileChecker } from '../interfaces/localfilechecker';
import { ILocalMergeFile, IS3File, MergeControllerService } from '../merge-component/merge-controller.service';
import { DocumentClientDatasourceApiListServiceFactory } from '../services/datasource/document-client-datasource-list.service';
import { FileListControllerService } from '../services/file-list.controller.service';
import { EditFileTask } from '../tasks/edit-file-task';

export interface IFileListViewConfig {
  readonly namespace: string;
  readonly fileListProvider: IFileListProvider;
  readonly fileDataSourceApiService: IFileDataSourceApiService;
  readonly workflowService: IWorkflowService;
  readonly namedEntityService: INamedEntityService;
  readonly iconService: IIconService;
  readonly fileToPathConverter: IFileToPathConverter;
  readonly localFileChecker: LocalFileChecker;
}

export interface IFileEditorController {
  fileOpened(file: IMergeFile | IS3File, remoteFileInfo: IRemoteFileInfo): void;

  fileModified(file: IMergeFile | IS3File, modifiedInfo: any): void;

  fileSaved(file: IMergeFile | IS3File, remoteFileInfo: IRemoteFileInfo): void;

  fileError(error: String, action: ActionMeta, srcFile: IMergeFile | IS3File): void;
}

@Component({
  selector: 'app-file-list-view',
  templateUrl: './file-list-view.component.html',
  styleUrls: ['./file-list-view.scss', './web-app.file-list-view.scss', './chrome-extension.file-list-view.scss'],
  standalone: false,
})
export class FileListViewComponent implements IFileEditorController, OnInit {
  @Input()
  config: IFileListViewConfig;

  @Input()
  filesListOpen = false;

  @Output() emitFilesListOpen = new EventEmitter<boolean>();

  @ViewChild(SimpleTableComponent) simpleTable?: SimpleTableComponent;

  @ViewChild('searchInputEl') searchInputEl?: ElementRef<HTMLInputElement>;

  renderMode = runtimeEnvironment.renderMode;

  files: ISimpleTableRow[] = [];

  fileSearchFn = (searchText: string, row: IMergeFile): boolean => row.name.toLowerCase().includes(searchText.toLowerCase());

  webAppColumnHeadings = [
    {
      title: 'Filename',
      width: '45%',
      sortFunction: FileListViewComponent.sortByName,
    },
    {
      title: 'Last Modified',
      width: '25%',
      sortFunction: FileListViewComponent.sortByModified,
    },
    {
      title: 'File size',
      width: '15%',
      sortFunction: FileListViewComponent.sortByFileSize,
    },
    { title: 'Actions', width: '15%' },
  ] as ISimpleTableColumn[];

  chromeExtensionColumnHeadings = [
    {
      title: 'Filename',
      width: '71%',
      sortFunction: FileListViewComponent.sortByName,
    },
    { title: 'Actions', width: '29%' },
  ] as ISimpleTableColumn[];
  columnHeadings = this.renderMode === 'web-app' ? this.webAppColumnHeadings : this.chromeExtensionColumnHeadings;

  entity: INamedEntity;

  showSpinner = false;
  navigateLabel: string;

  entitiesMeta: IEntityMetadata[];
  entityLabel: string;

  private static ACTION_EDIT = 'action-edit';
  private static ACTION_ADD_TO_MERGE_BASKET = 'action-add-to-merge-basket';
  private static ACTION_CONVERT_TO_PDF = 'action-convert-to-pdf';
  private static ACTION_CONVERT_TO_DOCX = 'action-convert-to-docx';

  private static EDIT_ACTION = {
    icon: 'edit_note',
    title: 'Edit in Word',
    meta: FileListViewComponent.ACTION_EDIT,
    iconClasses: 'icon-edit',
  };
  private static ADD_ACTION = {
    icon: 'add_circle_outline',
    title: 'Add to Merge Basket',
    meta: FileListViewComponent.ACTION_ADD_TO_MERGE_BASKET,
  };
  private static CONVERT_TO_PDF_ACTION = {
    icon: 'swap_horiz',
    title: 'Convert file to a PDF',
    meta: FileListViewComponent.ACTION_CONVERT_TO_PDF,
  };
  private static CONVERT_TO_DOCX_ACTION = {
    icon: 'swap_horiz',
    title: 'Convert file to a DOCX',
    meta: FileListViewComponent.ACTION_CONVERT_TO_DOCX,
  };
  private readonly extensions = ['.doc', '.docx', '.pdf', '.jpg', '.jpeg', '.png', '.gif'];
  private readonly editableExtensions = ['.doc', '.docx', '.pdf'];
  private readonly updateUi: (update) => void;

  private serviceProvider: ISharedServiceProviderService;

  constructor(
    private fileListControllerService: FileListControllerService,
    private mergeControllerService: MergeControllerService,
    private messageNotificationService: MessageNotificationService,
    private transformBytesToMB: TransformBytesToMB,
    private ngZone: NgZone,
    private datePipe: DatePipe,
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    private pendoService: PendoService,
    private fileExtensionMatcherService: FileExtensionMatcherService,
    private contentTypeMapperService: ContentTypeMapperService,
  ) {
    console.debug('FileListViewComponent created');
    this.serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.identityStorage.get()?.namespace);

    this.updateUi = runtimeEnvironment.runningAsChromeExtension ? this.updateUiInZone : this.updateUiNoZone;

    this.mergeControllerService.removeFromBasket$.subscribe((file) => this.fileRemovedFromBasket(file));

    this.mergeControllerService.saveFileToEntity$.subscribe((request) => this.saveFileToEntity(request.file, request.result));

    this.fileListControllerService.refresh$.subscribe((entity) => this.refreshEvent(entity));

    const terminologyService = this.serviceProvider.getTerminologyService();
    this.navigateLabel = new TerminologySentenceBuilder()
      .text('Navigate to ')
      .anarthrous._.name._.recordName.text(' to begin')
      .build(terminologyService);

    this.serviceProvider
      .getEntityMetadataService()
      .getEntitiesMetadata()
      .subscribe((entitiesMeta) => {
        this.entitiesMeta = entitiesMeta;
      });
  }

  ngOnInit() {
    this.config.namedEntityService.namedEntitySelected$.subscribe((entity) => this.namedEntitySelected(entity));
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + F to toggle the Files List
  // - Use Ctrl + Shift + M to focus the Files search box
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey && event.code === 'KeyF') {
      event.preventDefault();
      this.closeFileList();
      this.pendoService?.recordEvent('Alt+Shift+F - Toggle Files List');
    } else if (event.ctrlKey && event.shiftKey && event.code === 'KeyE') {
      event.preventDefault();
      this.focusFileSearch();
      this.pendoService?.recordEvent('Ctrl+Shift+E - Focus Files search box');
    }
  }

  onSearchInput(event: Event): void {
    this.simpleTable?.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.resetSearch();
    this.searchInputEl?.nativeElement?.focus();
  }

  private focusFileSearch(): void {
    if (!this.filesListOpen) {
      this.openFileList();
      setTimeout(() => this.searchInputEl?.nativeElement?.focus());
      return;
    }
    this.searchInputEl?.nativeElement?.focus();
  }

  private resetSearch(): void {
    if (this.searchInputEl?.nativeElement) {
      this.searchInputEl.nativeElement.value = '';
    }
    this.simpleTable?.search('');
  }

  itemActionClick(itemAction: { item: IMergeFile; action: string }): void {
    console.log('item ACTION click in file list view', itemAction);
    const srcFile = itemAction.item;
    if (itemAction.action === FileListViewComponent.ACTION_ADD_TO_MERGE_BASKET) {
      this.addFileToBasket(srcFile);
    } else if (itemAction.action === FileListViewComponent.ACTION_EDIT) {
      this.editFile(srcFile);
    } else {
      console.warn(`${itemAction?.action} not implemented.`);
    }
  }

  itemRowDoubleClick(item: IMergeFile): void {
    this.addFileToBasket(item);
  }

  openFileList(): void {
    if (!this.filesListOpen) {
      this.filesListOpen = true;
      this.emitFilesListOpen.emit(this.filesListOpen);
    }
  }

  closeFileList(): void {
    this.filesListOpen = !this.filesListOpen;
    this.emitFilesListOpen.emit(this.filesListOpen);
  }

  refreshFiles($event): void {
    $event.stopPropagation();
    this.namedEntitySelected(this.entity);
  }

  fileOpened(file: IMergeFile, remoteFileInfo: { editorHost: string; path: string; fileName: string }): void {
    console.log('File opened', file, remoteFileInfo);
    setTimeout(() => {
      this.setIsActionEnabled(true, FileListViewComponent.ACTION_EDIT);
    }, 5000); // arbitrary time for Word splash screen to complete.
  }

  fileModified(file: IMergeFile, modifiedInfo: any): void {
    console.log('File modified', file, modifiedInfo);
  }

  fileSaved(file: IMergeFile, modifiedInfo: any): void {
    console.log('File saved', file, modifiedInfo, this.entity);

    // a bug in Invenias means the id back is NOT the document id
    // refresh the entire list if we're on the same entity
    if (this.entity.type === modifiedInfo.path[0] && this.entity.id === modifiedInfo.path[1]) {
      console.log(`updating entity file list ${this.entity.type}: ${this.entity.id}`);
      // refresh the file list
      this.namedEntitySelected(this.entity);
    }

    // Refresh Invenias' Documents list
    if (this.renderMode === 'chrome-extension') {
      chrome.runtime.sendMessage({ message: 'refresh_docs' });
    }

    this.messageNotificationService.info(`Save success.`, `'${file.name}' saved to original record.`);
  }

  fileError(error: String, actionMeta: ActionMeta, srcFile: IMergeFile): void {
    this.messageNotificationService.error(`Error: ${srcFile.name}`, error);
    this.setIsActionEnabled(true, actionMeta);
  }

  getIcon(type: string) {
    return this.config.iconService.getIcon(type);
  }

  findEntityMeta(type: string) {
    return this.entitiesMeta?.find((e) => e.name.toLowerCase() === type?.toLowerCase());
  }

  // TODO: seems bit intense, how to do this whilst keeping simple table dumb?
  private setIsActionEnabled(setting: boolean, actionMeta: ActionMeta): void {
    this.files.forEach((f) => {
      const cellsWithActions = f.cells.filter((c) => c.actions);
      cellsWithActions.forEach((cell) => {
        cell.actions.forEach((a) => {
          if (a.meta === actionMeta) {
            a.disabled = !setting;
          }
        });
      });
    });
  }

  private static sortByName(a: IMergeFile, b: IMergeFile): number {
    return a.name.localeCompare(b.name);
  }

  private static sortByModified(a: IMergeFile, b: IMergeFile): number {
    return a.dateModified - b.dateModified;
  }

  private static sortByFileSize(a: IMergeFile, b: IMergeFile): number {
    return (a.size ?? 0) - (b.size ?? 0);
  }

  private addFileToBasket(srcFile: IMergeFile): void {
    const row = this.files.find((r) => r.data.id === srcFile.id);
    if (!row.disabled) {
      this.mergeControllerService.addToBasket(srcFile);
      this.setRowDisabled(row, true);
    }
  }

  private editFile(srcFile: IMergeFile): void {
    const namespace = this.config.namespace;
    const editTask = new EditFileTask(namespace, srcFile, this.config.fileToPathConverter, this);
    const taskRunner = new WorkflowTaskRunner(this.config.workflowService, editTask);

    taskRunner.run().subscribe(
      (_) => {
        this.messageNotificationService.info('Opening file', srcFile.name);
        this.setIsActionEnabled(false, FileListViewComponent.ACTION_EDIT);
      },
      (error) => {
        this.setIsActionEnabled(true, FileListViewComponent.ACTION_EDIT);
        this.messageNotificationService.error('The document could not be opened.', error);
      },
    );
  }

  private namedEntitySelected(namedEntity: INamedEntity): void {
    this.updateUi(() => (this.showSpinner = true));
    this.resetSearch();

    if (!namedEntity) {
      this.updateUi(() => {
        this.files = [];
        this.entity = undefined;
        this.updateUi(() => (this.showSpinner = false));
      });
      return;
    }

    this.entity = namedEntity;
    this.entityLabel = this.entitiesMeta.find((e) => e.name.toLowerCase() === namedEntity.type.toLowerCase())?.label;

    DocumentClientDatasourceApiListServiceFactory.instance
      .create(this.config.namespace, this.config.fileListProvider.dataSourceApiService)
      .list({
        entity: namedEntity,
        associatedType: this.config.fileListProvider.listType,
      })
      .subscribe(
        (resp) => {
          const normalisedFiles = resp.map((file) => this.contentTypeMapperService.normaliseFileExtension(file));

          const keep = normalisedFiles.filter((file) => {
            return this.fileExtensionMatcherService.matchesAllowedExtension(file, this.extensions);
          });

          const newFiles: IMergeFile[] = keep.map((item) => ({
            id: item.id,
            type: this.config.fileListProvider.fileType,
            name: item.name,
            belongsTo: namedEntity,
            size: item.size,
            dateModified: item.dateModified,
            downloadUrl: item.downloadUrl,
            origin: item.origin,
            contentType: item.contentType,
            fileExtension: item.fileExtension,
            contentSubType: item.contentSubType,
          }));

          this.mergeControllerService.getMergeFilesInBasket().subscribe((mergeFilesInBasket) => {
            const filesInBasket = mergeFilesInBasket.filter((mf) => {
              return mf.belongsTo?.id === this.entity.id && mf.belongsTo?.type === this.entity.type;
            });

            const tableRows = newFiles.map((f) => this.buildTableRow(f));
            tableRows.forEach((row) => {
              const inBasket = filesInBasket.findIndex((mf) => mf.id === row.data.id) !== -1;
              this.setRowDisabled(row, inBasket);
            });

            this.updateUi(() => (this.files = tableRows.sort(FileListViewComponent.fileSort)));

            this.updateUi(() => (this.showSpinner = false));
          });
        },
        (error) => {
          this.updateUi(() => (this.showSpinner = false));
          this.messageNotificationService.error('Error getting file list.', error);
        },
      );
  }

  private fileRemovedFromBasket(file: IMergeFile): void {
    const index = this.files.findIndex((f) => f.data.id === file.id);
    if (index > -1) {
      this.setRowDisabled(this.files[index], false);
    }
  }

  private saveFileToEntity(file: ILocalMergeFile, result: AsyncSubject<IMergeFile>): void {
    const existingFiles = this.files.map((f) => f.data);
    const fileCheck = this.config.localFileChecker(file, existingFiles);
    if (!fileCheck.ok) {
      const failReasonMsg =
        fileCheck.reason === FileCheckReason.AlreadyExists ? `The ${this.config.fileListProvider.fileType} already exists.` : undefined;
      this.messageNotificationService.info(`Cannot save to ${this.entity.name}`, failReasonMsg);
      result.complete();
      return;
    }

    this.config.fileDataSourceApiService
      .postFile(file.file, {
        entity: this.entity,
        fileType: this.config.fileListProvider.fileType,
      })
      .subscribe(
        (resp) => {
          console.log('postFile resp', resp);
          const newMergeFile: IMergeFile = {
            id: resp.id,
            type: resp.type,
            name: resp.name,
            belongsTo: this.entity,
            size: file.size,
          };
          const newRow = this.buildTableRow(newMergeFile); // assumption is basket asked for this change
          this.setRowDisabled(newRow, true);
          this.files.push(newRow);
          this.files.sort(FileListViewComponent.fileSort);
          result.next(newMergeFile);
          result.complete();
        },
        (error) => {
          this.messageNotificationService.error('Failed to save file to record.', error);
          result.error(error);
        },
        () => {
          result.complete();
        },
      );
  }

  private refreshEvent(entity: IEntity): void {
    console.log('refresh event on', entity?.id, ', current is ', this.entity?.id);

    // force a refresh of the list so the new file appears
    if (FileListViewComponent.isSameEntity(entity, this.entity)) {
      this.namedEntitySelected(this.entity);
    }
  }

  private buildTableRow(file: IMergeFile): ISimpleTableRow {
    const actions = [FileListViewComponent.ADD_ACTION];
    if (this.isEditable(file)) {
      actions.unshift(FileListViewComponent.EDIT_ACTION);
    }
    // if (file.name.toLowerCase().endsWith('pdf'))
    //   actions.unshift(FileListViewComponent.CONVERT_TO_DOCX_ACTION);
    // else if (file.name.toLowerCase().endsWith('docx') || file.name.toLowerCase().endsWith('doc'))
    //   actions.unshift(FileListViewComponent.CONVERT_TO_PDF_ACTION);
    const cells =
      this.renderMode === 'web-app'
        ? [
            { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
            { text: this.datePipe.transform(file.dateModified, 'medium') },
            { text: this.transformBytesToMB.transform(file.size) },
            { actions: actions },
          ]
        : [{ autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension }, { actions: actions }];
    return { cells: cells, data: file };
  }

  private setRowDisabled(row: ISimpleTableRow, disabled: boolean): void {
    row.disabled = disabled;
    row.rowTooltipText = disabled ? 'File in the Merge Basket' : undefined;
  }

  private isEditable(file: IMergeFile): boolean {
    return this.fileExtensionMatcherService.matchesAllowedExtension(file, this.editableExtensions);
  }

  private updateUiNoZone(update: () => void): void {
    update();
  }

  private updateUiInZone(update: () => void): void {
    this.ngZone.run(() => update());
  }

  private static fileSort(a: ISimpleTableRow, b: ISimpleTableRow): number {
    if (a.data.name.toLowerCase() < b.data.name.toLowerCase()) {
      return -1;
    }
    if (a.data.name.toLowerCase() > b.data.name.toLowerCase()) {
      return 1;
    }
    return 0;
  }

  private static isSameEntity(a: IEntity | undefined, b: IEntity | undefined): boolean {
    return a?.id === b?.id && a?.type === b?.type;
  }
}
